import { apiRequest } from '../../shared/api/client';
import type {
  TelegramAccountsResponse,
  TelegramBindingListResponse,
  TelegramChatBinding,
  TelegramConnectCodeResponse,
  TelegramFieldMapping,
  TelegramNotifyPreference,
  TelegramNotifySettingsResponse,
  TelegramSpaceNotifySettings,
  UpdateTelegramBindingRequest,
} from '@lens/shared';

/** Клиент модуля Telegram (docs/modules/telegram-bot.md). */

export function listTelegramBindings(spaceId: string): Promise<TelegramBindingListResponse> {
  return apiRequest<TelegramBindingListResponse>(`/api/spaces/${spaceId}/telegram/bindings`);
}

export function createTelegramConnectCode(spaceId: string): Promise<TelegramConnectCodeResponse> {
  return apiRequest<TelegramConnectCodeResponse>(`/api/spaces/${spaceId}/telegram/connect-code`, {
    method: 'POST',
  });
}

export function updateTelegramBinding(
  spaceId: string,
  bindingId: string,
  body: UpdateTelegramBindingRequest,
): Promise<TelegramChatBinding> {
  return apiRequest<TelegramChatBinding>(`/api/spaces/${spaceId}/telegram/bindings/${bindingId}`, {
    method: 'PATCH',
    body,
  });
}

export function deleteTelegramBinding(spaceId: string, bindingId: string): Promise<void> {
  return apiRequest<void>(`/api/spaces/${spaceId}/telegram/bindings/${bindingId}`, {
    method: 'DELETE',
  });
}

export function createTelegramServiceFields(
  spaceId: string,
  itemTypeId: string,
  fields: string[],
): Promise<{ mapping: Partial<TelegramFieldMapping>; created: string[] }> {
  return apiRequest(`/api/spaces/${spaceId}/telegram/service-fields`, {
    method: 'POST',
    body: { itemTypeId, fields },
  });
}

export interface TelegramMeResponse {
  linked: boolean;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  linkedAt: string | null;
  botUsername: string | null;
}

export function getTelegramMe(): Promise<TelegramMeResponse> {
  return apiRequest<TelegramMeResponse>('/api/telegram/me');
}

export function unlinkTelegram(): Promise<void> {
  return apiRequest<void>('/api/telegram/link', { method: 'DELETE' });
}

/** Данные кнопки привязки приходят от Telegram уже подписанными — просто передаём их дальше. */
export function linkTelegram(payload: Record<string, string>): Promise<{
  status: string;
  backfilledItems: number;
}> {
  return apiRequest('/api/telegram/link', { method: 'POST', body: payload });
}

export function listTelegramAccounts(): Promise<TelegramAccountsResponse> {
  return apiRequest<TelegramAccountsResponse>('/api/admin/telegram/accounts');
}

export function linkTelegramAccounts(
  links: Array<{ telegramUserId: string; userId: string }>,
): Promise<{ linked: number; skipped: number }> {
  return apiRequest('/api/admin/telegram/accounts/link', { method: 'POST', body: { links } });
}

export function importTelegramAccounts(
  accounts: Array<{ id: string; firstName: string | null; lastName: string | null }>,
): Promise<{ imported: number }> {
  return apiRequest('/api/admin/telegram/accounts/import', { method: 'POST', body: { accounts } });
}

export interface TelegramLinkCodeAccount {
  telegramUserId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
}

/** Привязка по персональной ссылке от бота (когда кнопка входа недоступна). */
export function getTelegramLinkCode(code: string): Promise<{ account: TelegramLinkCodeAccount }> {
  return apiRequest(`/api/telegram/link/${encodeURIComponent(code)}`);
}

export function linkTelegramByCode(code: string): Promise<{ status: string; backfilledItems: number }> {
  return apiRequest(`/api/telegram/link/${encodeURIComponent(code)}`, { method: 'POST' });
}

/** Подтянуть аватарки тем, кто связал аккаунт, но остался без фото. */
export function backfillTelegramAvatars(): Promise<{ checked: number; updated: number }> {
  return apiRequest('/api/admin/telegram/accounts/avatars', { method: 'POST' });
}

// ─── Личные уведомления в Telegram ──────────────────────────────────────

export function getSpaceTelegramNotify(spaceId: string): Promise<TelegramSpaceNotifySettings> {
  return apiRequest<TelegramSpaceNotifySettings>(`/api/spaces/${spaceId}/telegram/notifications`);
}

export function setSpaceTelegramNotify(
  spaceId: string,
  enabled: boolean,
): Promise<TelegramSpaceNotifySettings> {
  return apiRequest<TelegramSpaceNotifySettings>(`/api/spaces/${spaceId}/telegram/notifications`, {
    method: 'PUT',
    body: { enabled },
  });
}

export function getMyTelegramNotify(): Promise<TelegramNotifySettingsResponse> {
  return apiRequest<TelegramNotifySettingsResponse>('/api/telegram/me/notifications');
}

export function setMyTelegramNotify(
  body: TelegramNotifyPreference,
): Promise<TelegramNotifySettingsResponse> {
  return apiRequest<TelegramNotifySettingsResponse>('/api/telegram/me/notifications', {
    method: 'PUT',
    body,
  });
}
